import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * Nominations & Party Tickets.
 *
 * ## Two audiences, two shapes
 *
 * Everything under `/nominations` is for party officials and is RBAC-guarded. Everything
 * under `/member` is for a party member holding a 15-minute voting session — a different
 * principal on a different filter chain, with no authorities at all. They are kept apart
 * here for the same reason they are kept apart on the server.
 */

// ── contests ─────────────────────────────────────────────────────────────────────────────

export interface Nomination {
  id: string
  electionId: string
  partyId: string
  partyName: string | null
  partyShortName: string | null
  partyColourHex: string | null
  seatTypeCode: string
  unitId: string
  unitName: string | null
  unitTypeCode: string | null
  unitPath: string | null
  title: string
  methodCode: string
  statusCode: string
  /** Membership on or before this counts. The anti-stacking control. */
  rollCutoffAt: string
  opensAt: string
  closesAt: string
  winThreshold: number | null
  reservedForCode: string | null
  allowUssd: boolean
  allowOnline: boolean
  allowAssisted: boolean
  rollFrozenAt: string | null
  closedAt: string | null
  /** The FROZEN ROLL — not the register, not the membership. 0 until frozen. */
  eligibleVoters: number
  votesCast: number
  turnoutPct: number | null
  aspirants: number
  createdAt: string
}

export interface Membership {
  id: string
  voterId: string
  voterName: string | null
  partyId: string
  partyName: string | null
  memberNumber: string | null
  statusCode: string
  joinedOn: string
  leftOn: string | null
  leftReasonCode: string | null
  sourceCode: string
  unitPath: string | null
  verifiedAt: string | null
  createdAt: string
}

export interface Aspirant {
  id: string
  nominationId: string
  candidateId: string
  ballotName: string
  /** Assigned by a random draw, never by application order. */
  ballotPosition: number | null
  statusCode: string
  appliedAt: string
  feeAmount: number | null
  feePaidAt: string | null
  feeReference: string | null
  clearedAt: string | null
  rejectionReason: string | null
  withdrawnAt: string | null
  symbolFileId: string | null
}

export interface NominationResult {
  nominationId: string
  aspirantId: string
  ballotName: string | null
  votes: number
  sharePct: number | null
  position: number
  outcomeCode: string
}

export interface Ticket {
  id: string
  nominationId: string
  aspirantId: string
  ballotName: string | null
  /** The election_candidacy this created — nominations feeding tallying. */
  candidacyId: string | null
  awardedAt: string
  revokedAt: string | null
  revokeReason: string | null
}

export interface RollEntry {
  id: string
  voterId: string
  voterName: string | null
  delegate: boolean
  weight: number
  voted: boolean
  votedAt: string | null
  voteChannelCode: string | null
}

export const listNominations = (p: {
  electionId: string
  partyId?: string | null
  status?: string | null
  size?: number
  offset?: number
}) => api.get<Nomination[]>(`/nominations${qs({ ...p })}`)

export const getNomination = (id: string) => api.get<Nomination>(`/nominations/${id}`)

export const createNomination = (body: {
  electionId: string
  partyId: string
  seatTypeCode: string
  unitId: string
  title: string
  methodCode?: string
  rollCutoffAt: string
  opensAt: string
  closesAt: string
  winThreshold?: number | null
  reservedForCode?: string | null
  allowUssd?: boolean
  allowOnline?: boolean
  allowAssisted?: boolean
}) => api.post<Nomination>('/nominations', body)

export const openRoll = (id: string) =>
  api.post<Nomination>(`/nominations/${id}/open-roll`)

/** Fixes the electorate. After this the roll never changes — that is the whole point. */
export const freezeRoll = (id: string) =>
  api.post<{ nominationId: string; eligibleInserted: number }>(`/nominations/${id}/freeze-roll`)

export const nominationRoll = (id: string) =>
  api.get<RollEntry[]>(`/nominations/${id}/roll`)

export const setDelegate = (id: string, body: {
  rollRowId: string; delegate: boolean; weight?: number | null
}) => api.post<RollEntry>(`/nominations/${id}/roll/delegate`, body)

export const openVoting = (id: string) =>
  api.post<Nomination>(`/nominations/${id}/open-voting`)

export const closeVoting = (id: string) =>
  api.post<Nomination>(`/nominations/${id}/close-voting`)

export const tallyNomination = (id: string) =>
  api.post<NominationResult[]>(`/nominations/${id}/tally`)

export const nominationResults = (id: string) =>
  api.get<NominationResult[]>(`/nominations/${id}/results`)

export const awardTicket = (id: string, aspirantId: string) =>
  api.post<Ticket>(`/nominations/${id}/award`, { aspirantId })

export const revokeTicket = (id: string, reason: string) =>
  api.post<Ticket>(`/nominations/${id}/revoke-ticket`, { reason })

export const ticketHistory = (id: string) =>
  api.get<Ticket[]>(`/nominations/${id}/tickets`)

export const disputeNomination = (id: string, reason: string) =>
  api.post<Nomination>(`/nominations/${id}/dispute`, { reason })

export const cancelNomination = (id: string, reason: string) =>
  api.post<Nomination>(`/nominations/${id}/cancel`, { reason })

// ── membership ───────────────────────────────────────────────────────────────────────────

export const listMemberships = (p: {
  partyId?: string | null
  status?: string | null
  scopePath?: string | null
  size?: number
  offset?: number
}) => api.get<Membership[]>(`/nominations/memberships${qs({ ...p })}`)

export const createMembership = (body: {
  nationalId: string
  partyId: string
  memberNumber?: string | null
  joinedOn?: string | null
  verifyIdentity?: boolean
}) => api.post<Membership>('/nominations/memberships', body)

/** Closes one membership and opens another, atomically. Never two active. */
export const transferMembership = (body: {
  nationalId: string; toPartyId: string; memberNumber?: string | null
}) => api.post<Membership>('/nominations/memberships/transfer', body)

export const closeMembership = (id: string, body: { expelled: boolean; reason: string }) =>
  api.post<Membership>(`/nominations/memberships/${id}/close`, body)

export interface ImportOutcome {
  rows: number
  created: number
  dryRun: boolean
  problems: { nationalId: string; problem: string }[]
}

/** Dry run by default — the interesting output is the problems, not the successes. */
export const importMemberships = (body: {
  partyId: string
  dryRun: boolean
  rows: { nationalId: string; memberNumber?: string | null; joinedOn?: string | null }[]
}) => api.post<ImportOutcome>('/nominations/memberships/import', body)

export const nominationVocabulary = () =>
  api.get<Record<string, { code: string; label: string }[]>>(
    '/nominations/memberships/vocabulary')

// ── aspirants ────────────────────────────────────────────────────────────────────────────

export const listAspirants = (nominationId: string) =>
  api.get<Aspirant[]>(`/nominations/${nominationId}/aspirants`)

export const registerAspirant = (nominationId: string, body: {
  candidateId: string; ballotName?: string | null; feeAmount?: number | null
}) => api.post<Aspirant>(`/nominations/${nominationId}/aspirants`, body)

export const recordFee = (nominationId: string, aspirantId: string, reference: string) =>
  api.post<Aspirant>(`/nominations/${nominationId}/aspirants/${aspirantId}/fee`, { reference })

export const startVetting = (nominationId: string, aspirantId: string) =>
  api.post<Aspirant>(`/nominations/${nominationId}/aspirants/${aspirantId}/start-vetting`)

export const clearAspirant = (nominationId: string, aspirantId: string) =>
  api.post<Aspirant>(`/nominations/${nominationId}/aspirants/${aspirantId}/clear`)

export const rejectAspirant = (nominationId: string, aspirantId: string, reason: string) =>
  api.post<Aspirant>(`/nominations/${nominationId}/aspirants/${aspirantId}/reject`, { reason })

export const withdrawAspirant = (nominationId: string, aspirantId: string) =>
  api.post<Aspirant>(`/nominations/${nominationId}/aspirants/${aspirantId}/withdraw`)

/**
 * Draw ballot positions.
 *
 * No seed parameter, deliberately: the server draws from SecureRandom and records the seed
 * in the audit trail. A caller-supplied seed would let an official redraw until the order
 * suited them — reproducibility afterwards is the goal, not control beforehand.
 */
export const drawPositions = (nominationId: string) =>
  api.post<Aspirant[]>(`/nominations/${nominationId}/aspirants/draw`)

export const assistedVote = (nominationId: string, body: {
  nationalId: string; aspirantId: string
}) => api.post<{ nominationId: string; receipt: string }>(
  `/nominations/${nominationId}/assisted-vote`, body)

// ── analytics ────────────────────────────────────────────────────────────────────────────

export interface NominationAnalytics {
  summary: {
    nominationId: string
    title: string
    statusCode: string
    eligible: number
    voted: number
    turnoutPct: number | null
    assisted: number
    assistedPct: number | null
    opensAt: string
    closesAt: string
  }
  byPlace: {
    unitPath: string
    unitName: string
    unitTypeCode: string | null
    eligible: number
    voted: number
    turnoutPct: number | null
    assisted: number
  }[]
  curve: { bucket: string; votes: number; ussd: number; online: number; assisted: number }[]
  channels: { channelCode: string; votes: number; sharePct: number | null }[]
  /** Not accusations. Every one has an innocent reading too — see the server comment. */
  flags: {
    code: string
    severity: string
    nominationId: string
    nominationTitle: string
    where: string
    detail: string
  }[]
}

export const nominationAnalytics = (id: string) =>
  api.get<NominationAnalytics>(`/nominations/${id}/analytics`)

// ── certificates ─────────────────────────────────────────────────────────────────────────

export interface Certificate {
  id: string
  nominationId: string
  ticketId: string
  aspirantId: string
  /** Printed on the PDF and typed into the verifier. Unguessable, never sequential. */
  reference: string
  /** Null when the render succeeded but storage did not — visible as broken, not hidden. */
  fileId: string | null
  contentSha256: string | null
  issuedAt: string
  revokedAt: string | null
  revokeReason: string | null
  live: boolean
}

export const listCertificates = (nominationId: string) =>
  api.get<Certificate[]>(`/nominations/${nominationId}/certificates`)

export const issueCertificate = (nominationId: string) =>
  api.post<Certificate>(`/nominations/${nominationId}/certificates`)

/** A presigned, ten-minute link straight to the object store. */
export const certificateDownloadUrl = (nominationId: string, certificateId: string) =>
  api.get<{ url: string }>(
    `/nominations/${nominationId}/certificates/${certificateId}/download`)

export const revokeCertificate = (
  nominationId: string, certificateId: string, reason: string,
) => api.post<Certificate>(
  `/nominations/${nominationId}/certificates/${certificateId}/revoke`, { reason })
